<template>
  <div class="card-page">
    <!-- 背景装饰 -->
    <div class="bg-gradient"></div>
    <div class="orb orb-1"></div>
    <div class="orb orb-2"></div>
    <div class="orb orb-3"></div>

    <div class="content-wrapper">
      <aside class="sidebar">
        <nav class="nav-card">
          <div class="nav-logo">
            <div class="logo-icon">
              <img :src="logo2Image" alt="" class="logo2-icon" />
            </div>
            <h1 class="brand-name">树苗英语</h1>
          </div>

          <ul class="nav-list">
            <li v-for="item in navItems" :key="item.id">
              <a
                href="#"
                @click.prevent="handleNavItemClick(item)"
                :class="['nav-item', { active: activeNav === item.id && item.id !== 'logout' }]"
              >
                <font-awesome-icon :icon="item.icon" class="nav-icon" />
                <span>{{ item.label }}</span>
              </a>
            </li>
          </ul>
        </nav>
      </aside>

      <!-- 主内容 -->
      <main class="main-content">
        <!-- 1️⃣ 顶部 Header -->
        <header class="page-header" :class="{ 'is-scrolled': isHeaderScrolled }">
          <button type="button" class="mobile-back-btn" @click="handleGoBack" aria-label="返回上一页">
            <font-awesome-icon icon="chevron-left" />
          </button>
          <h1 class="main-title">
            26个英文字母
          </h1>
        </header>
        <div class="page-header-spacer" aria-hidden="true"></div>

        <!-- 3️⃣ 卡片列表区 -->
        <div class="cards-grid">
          <div
            v-for="(card, index) in filteredCards"
            :key="card.id"
            class="word-card"
            :class="{ playing: currentPlayingIndex === index }"
            @click="handleCardClick(card, index)"
            :ref="el => { if (isAutoPlaying && currentPlayingIndex === index) scrollToCard(el) }"
          >
            <div class="card-inner">
              <!-- 图标区域 -->
              <div class="card-icon-wrap">
                <div class="card-icon" :style="{ background: card.bgColor }">
                  <span class="icon-emoji">{{ card.icon }}</span>
                </div>
              </div>

              <!-- 英文 -->
              <h3 class="card-en">{{ card.en }}</h3>

              <!-- 中文 -->
              <p class="card-zh" :class="{ hidden: !showTranslation }">{{ card.zh }}</p>
            </div>
          </div>
        </div>
      </main>
    </div>

    <!-- 5️⃣ 底部 Dock 控制栏 -->
    <div class="dock-bar">
      <button class="dock-item" :class="{ active: !showTranslation }" @click="toggleTranslation">
        <font-awesome-icon icon="language" />
        <span>关闭翻译</span>
      </button>

      <button class="dock-item" @click="goToFullscreen">
        <font-awesome-icon icon="expand" />
        <span>全屏模式</span>
      </button>

      <button class="dock-item main-btn" @click="toggleAutoPlay">
        <font-awesome-icon :icon="isAutoPlaying ? 'pause' : 'play'" />
      </button>

      <button class="dock-item" @click="openSettings">
        <font-awesome-icon icon="cog" />
        <span>显示设置</span>
      </button>

      <button class="dock-item" @click="openTestMode">
        <font-awesome-icon icon="clipboard-check" />
        <span>测试练习</span>
      </button>
    </div>

    <!-- 4️⃣ 全屏沉浸模式 -->
    <Transition name="fullscreen">
      <div
        v-if="fullscreenCard"
        class="fullscreen-overlay"
        @click.self="closeFullscreen"
      >
        <div class="fullscreen-card">
          <!-- 背景模糊层 -->
          <div class="fullscreen-bg" :style="{ background: fullscreenCard.bgColor }"></div>

          <!-- 关闭按钮 -->
          <button class="close-btn" @click="closeFullscreen">
            <font-awesome-icon icon="times" />
          </button>

          <!-- 内容区 -->
          <div class="fullscreen-content">
            <!-- 上方图标 -->
            <div class="fs-icon-wrap">
              <div class="fs-icon" :style="{ background: fullscreenCard.bgColor }">
                <span class="fs-emoji">{{ fullscreenCard.icon }}</span>
              </div>
            </div>

            <!-- 英文大字 -->
            <h1 class="fs-en" @click="speakWord(fullscreenCard.en)">
              {{ fullscreenCard.en }}
            </h1>

            <!-- 中文翻译 -->
            <p class="fs-zh" :class="{ hidden: !showTranslation }">
              {{ fullscreenCard.zh }}
            </p>

            <!-- 发音提示 -->
            <p class="fs-hint">
              <font-awesome-icon icon="hand-pointer" class="hint-icon" />
              点击卡片即可发音
            </p>
          </div>

          <!-- 左右切换 -->
          <button class="nav-btn prev" @click.stop="prevCard">
            <font-awesome-icon icon="chevron-left" />
          </button>
          <button class="nav-btn next" @click.stop="nextCard">
            <font-awesome-icon icon="chevron-right" />
          </button>

          <!-- 进度 -->
          <div class="fs-progress">
            {{ currentIndex + 1 }} / {{ filteredCards.length }}
          </div>
        </div>
      </div>
    </Transition>

    <ParentPasswordModal ref="parentPasswordModalRef" />
  </div>
</template>

<script setup>
import logo2Image from '../assets/logo2.png'
import ParentPasswordModal from '../components/ParentPasswordModal.vue'
import { ref, computed, onMounted, onUnmounted } from 'vue'
import { useRouter } from 'vue-router'

const router = useRouter()

const activeNav = ref('')
const showTranslation = ref(true)
const isAutoPlaying = ref(false)
const fullscreenCard = ref(null)
const currentPlayingIndex = ref(-1)
const isHeaderScrolled = ref(false)
const pausedIndex = ref(-1) // 保存暂停时的索引
const AUTH_STORAGE_KEY = 'tree-sprout-auth'
const PARENT_VERIFIED_STORAGE_KEY = 'tree-sprout-parent-verified'
const parentPasswordModalRef = ref(null)
let autoPlayTimer = null

const navItems = [
  { id: 'home', label: '首页', icon: 'home' },
  { id: 'history', label: '观看历史', icon: 'play-circle' },
  { id: 'favorites', label: '宝贝收藏', icon: 'heart' },
  { id: 'settings', label: '偏好设置', icon: 'cog' },
  { id: 'parent', label: '家长模式', icon: 'user-shield' },
]

navItems.push({ id: 'logout', label: '登出账号', icon: 'right-from-bracket' })

const letterBgColors = [
  'rgba(255, 140, 66, 0.15)',
  'rgba(91, 143, 249, 0.15)',
  'rgba(76, 175, 80, 0.15)',
  'rgba(255, 193, 7, 0.15)',
  'rgba(156, 39, 176, 0.15)',
  'rgba(0, 150, 136, 0.15)',
]

// 词卡数据
const cards = ref(
  'ABCDEFGHIJKLMNOPQRSTUVWXYZ'.split('').map((letter, index) => ({
    id: index + 1,
    en: letter,
    zh: `字母 ${letter}`,
    icon: `${letter}${letter.toLowerCase()}`,
    bgColor: letterBgColors[index % letterBgColors.length],
  }))
)

// 过滤后的卡片
const filteredCards = computed(() => {
  return cards.value
})

// 当前卡片索引
const currentIndex = computed(() => {
  if (!fullscreenCard.value) return 0
  return filteredCards.value.findIndex(c => c.id === fullscreenCard.value.id)
})

// 关闭全屏
const closeFullscreen = () => {
  fullscreenCard.value = null
  stopAutoPlay()
}

// 上一张
const prevCard = () => {
  const idx = currentIndex.value
  const prev = (idx - 1 + filteredCards.value.length) % filteredCards.value.length
  fullscreenCard.value = filteredCards.value[prev]
  speakWord(fullscreenCard.value.en)
}

// 下一张
const nextCard = () => {
  const idx = currentIndex.value
  const next = (idx + 1) % filteredCards.value.length
  fullscreenCard.value = filteredCards.value[next]
  speakWord(fullscreenCard.value.en)
}

// 发音
const speakWord = (text) => {
  if ('speechSynthesis' in window) {
    speechSynthesis.cancel()
    const utterance = new SpeechSynthesisUtterance(text)
    utterance.lang = 'en-US'
    utterance.rate = 0.85
    speechSynthesis.speak(utterance)
  }
}

const handleGoBack = () => {
  if (window.history.length > 1) {
    router.back()
    return
  }

  router.push('/category')
}

const handleLogout = () => {
  window.localStorage.removeItem(AUTH_STORAGE_KEY)
  window.sessionStorage.removeItem(AUTH_STORAGE_KEY)
  window.sessionStorage.removeItem(PARENT_VERIFIED_STORAGE_KEY)
  router.replace('/login')
}

const openParentPasswordModal = () => {
  parentPasswordModalRef.value?.open()
}

const handleNavItemClick = (item) => {
  if (item.id === 'logout') {
    handleLogout()
    return
  }

  if (item.id === 'home') {
    router.push('/home')
    return
  }

  if (item.id === 'parent') {
    openParentPasswordModal()
    return
  }

  if (item.id === 'settings') {
    router.push('/preference')
    return
  }

  if (item.id === 'history' || item.id === 'favorites') {
    router.push({
      path: '/history',
      query: item.id === 'favorites' ? { tab: 'favorites' } : {}
    })
  }
}

// 滚动到当前播放的卡片
const scrollToCard = (el) => {
  if (el) {
    el.scrollIntoView({ behavior: 'smooth', block: 'center' })
  }
}

// 切换翻译显示
const toggleTranslation = () => {
  showTranslation.value = !showTranslation.value
}



// 跳转到全屏页面
const goToFullscreen = () => {
  router.push('/fullscreen')
}

// 自动播放
const toggleAutoPlay = () => {
  if (isAutoPlaying.value) {
    stopAutoPlay()
  } else {
    startAutoPlay()
  }
}

const startAutoPlay = () => {
  if (filteredCards.value.length > 0) {
    // 如果有暂停记录，从暂停位置继续；否则从第一个开始
    if (pausedIndex.value >= 0) {
      currentPlayingIndex.value = pausedIndex.value
    } else {
      currentPlayingIndex.value = 0
    }
    
    const currentCard = filteredCards.value[currentPlayingIndex.value]
    speakWord(currentCard.en)
    isAutoPlaying.value = true
    
    // 设置定时器，每3秒播放下一个
    autoPlayTimer = setInterval(() => {
      const nextIndex = (currentPlayingIndex.value + 1) % filteredCards.value.length
      currentPlayingIndex.value = nextIndex
      const nextCardItem = filteredCards.value[nextIndex]
      speakWord(nextCardItem.en)
    }, 3000)
  }
}

const stopAutoPlay = () => {
  isAutoPlaying.value = false
  // 保存当前播放位置
  pausedIndex.value = currentPlayingIndex.value
  if (autoPlayTimer) {
    clearInterval(autoPlayTimer)
    autoPlayTimer = null
  }
}

// 占位功能
const openSettings = () => {
  alert('设置功能开发中...')
}

const openTestMode = () => {
  alert('测试模式开发中...')
}

// 键盘事件
const handleKeydown = (e) => {
  if (!fullscreenCard.value) return

  if (e.key === 'Escape') {
    closeFullscreen()
  } else if (e.key === 'ArrowLeft') {
    prevCard()
  } else if (e.key === 'ArrowRight') {
    nextCard()
  } else if (e.key === ' ') {
    e.preventDefault()
    speakWord(fullscreenCard.value.en)
  }
}

const handleCardClick = (card, index) => {
  currentPlayingIndex.value = index
  pausedIndex.value = index
  speakWord(card.en)
}

const handlePageScroll = () => {
  isHeaderScrolled.value = window.scrollY > 12
}

onMounted(() => {
  window.addEventListener('keydown', handleKeydown)
  window.addEventListener('scroll', handlePageScroll, { passive: true })
  handlePageScroll()
})

onUnmounted(() => {
  window.removeEventListener('keydown', handleKeydown)
  window.removeEventListener('scroll', handlePageScroll)
  stopAutoPlay()
})
</script>

<style scoped>
/* 基础变量 */
:root {
  --primary: #FF8C42;
  --bg: #F9F7F2;
  --text: #2D3436;
  --text-light: #666;
}

/* 页面容器 */
.card-page {
  min-height: 100vh;
  background: var(--bg);
  position: relative;
  overflow-x: hidden;
  padding-bottom: 100px;
  font-family: 'Nunito', 'Source Han Sans CN', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
}

/* 背景渐变 */
.bg-gradient {
  position: fixed;
  inset: 0;
  background: linear-gradient(135deg, #FFF8F0 0%, #F9F7F2 50%, #F5F0E8 100%);
  z-index: -3;
}

/* 光晕装饰 */
.orb {
  position: fixed;
  border-radius: 50%;
  filter: blur(100px);
  opacity: 0.6;
  z-index: -2;
  animation: float 8s ease-in-out infinite;
}

.orb-1 {
  width: 500px;
  height: 500px;
  background: linear-gradient(135deg, #FFE4D4, #FFD4C4);
  top: -150px;
  right: -100px;
}

.orb-2 {
  width: 400px;
  height: 400px;
  background: linear-gradient(135deg, #E8F4FF, #D4E8FF);
  bottom: -100px;
  left: -100px;
  animation-delay: -3s;
}

.orb-3 {
  width: 300px;
  height: 300px;
  background: linear-gradient(135deg, #FFEAA8, #FFD88A);
  top: 50%;
  left: 30%;
  transform: translateY(-50%);
  animation-delay: -5s;
  opacity: 0.4;
}

@keyframes float {
  0%, 100% { transform: translate(0, 0) scale(1); }
  33% { transform: translate(30px, -30px) scale(1.05); }
  66% { transform: translate(-20px, 20px) scale(0.95); }
}

.content-wrapper {
  display: flex;
  align-items: stretch;
  width: 100%;
  min-height: 100vh;
}

.sidebar {
  width: 280px;
  flex-shrink: 0;
  height: 100vh;
  overflow-y: auto;
  background: #FFF9F5;
  box-shadow: 4px 0 24px rgba(0, 0, 0, 0.06);
  position: sticky;
  top: 0;
  z-index: 2;
}

.nav-card {
  min-height: 100%;
  padding: 0 0 20px;
  display: flex;
  flex-direction: column;
}

.nav-logo {
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 24px 20px;
  border-bottom: 1px solid rgba(255, 138, 61, 0.15);
}

.nav-logo .logo-icon {
  width: 48px;
  height: 48px;
  background: #fff;
  border-radius: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 4px 12px rgba(140, 104, 68, 0.12);
}

.nav-logo .logo2-icon {
  display: block;
  width: 34px;
  height: 34px;
  object-fit: contain;
}

.nav-logo .brand-name {
  font-size: 22px;
  font-weight: 800;
  color: #2D3436;
  letter-spacing: 2px;
}

.nav-list {
  list-style: none;
  display: flex;
  flex-direction: column;
  gap: 20px;
  padding: 16px 12px 0;
}

.nav-item {
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 18px;
  border-radius: 14px;
  color: #666;
  font-size: 15px;
  font-weight: 500;
  transition: all 0.3s ease;
  cursor: pointer;
  position: relative;
  text-decoration: none;
}

.nav-item:hover {
  background: rgba(255, 138, 61, 0.08);
  color: #FF8A3D;
}

.nav-item.active {
  background: linear-gradient(135deg, #FFE4D4 0%, #FFD4C4 100%);
  color: #FF6B35;
  font-weight: 700;
  box-shadow: 0 4px 16px rgba(255, 138, 61, 0.2);
}

.nav-icon {
  font-size: 18px;
  width: 24px;
  text-align: center;
}

/* 主内容区 */
.main-content {
  flex: 1;
  min-width: 0;
  width: 100%;
  max-width: 1400px;
  margin: 0 auto;
  padding: 60px 40px;
  position: relative;
  z-index: 1;
}

/* 1️⃣ 顶部 Header */
.page-header {
  position: relative;
  text-align: center;
  margin-bottom: 32px;
}

.page-header-spacer {
  display: none;
}

.mobile-back-btn {
  display: none;
  position: absolute;
  left: 0;
  top: 50%;
  transform: translateY(-50%);
  width: 44px;
  height: 44px;
  border: 1px solid rgba(255, 255, 255, 0.85);
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.82);
  backdrop-filter: blur(14px);
  -webkit-backdrop-filter: blur(14px);
  color: #5f5044;
  font-size: 16px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.08);
  cursor: pointer;
  align-items: center;
  justify-content: center;
}

.main-title {
  font-size: 64px;
  font-weight: 900;
  color: var(--text);
  letter-spacing: 4px;
  line-height: 1;
}

.main-title .highlight {
  color: var(--primary);
  position: relative;
  display: inline-block;
}

.main-title .highlight::after {
  content: '';
  position: absolute;
  bottom: 8px;
  left: 0;
  width: 100%;
  height: 12px;
  background: linear-gradient(135deg, rgba(255, 140, 66, 0.3), rgba(255, 107, 53, 0.2));
  border-radius: 6px;
  z-index: -1;
}

/* 3️⃣ 卡片列表区 */
.cards-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 40px;
}

/* 玻璃拟态卡片 */
.word-card {
  background: rgba(255, 255, 255, 0.6);
  backdrop-filter: blur(20px);
  -webkit-backdrop-filter: blur(20px);
  border: 1px solid rgba(255, 255, 255, 0.8);
  border-radius: 50px;
  padding: 40px 30px;
  cursor: pointer;
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  position: relative;
  overflow: hidden;
  min-height: 320px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.word-card::before {
  content: '';
  position: absolute;
  top: 0;
  left: -100%;
  width: 100%;
  height: 100%;
  background: linear-gradient(
    90deg,
    transparent,
    rgba(255, 255, 255, 0.4),
    transparent
  );
  transition: left 0.6s ease;
}

.word-card:hover::before {
  left: 100%;
}

.word-card:hover {
  transform: translateY(-12px) scale(1.02);
  box-shadow:
    0 30px 60px rgba(0, 0, 0, 0.1),
    0 10px 30px rgba(0, 0, 0, 0.06);
}

.word-card:hover .card-icon {
  transform: scale(1.15);
}

.word-card.playing {
  transform: scale(1.05);
  box-shadow: 0 20px 60px rgba(255, 140, 66, 0.3);
  border: 2px solid #FF8C42;
  animation: pulse 1s ease-in-out infinite;
}

.word-card.playing .card-en {
  color: #FF8C42;
}

.word-card.playing .card-icon {
  transform: scale(1.2);
}

@keyframes pulse {
  0%, 100% {
    box-shadow: 0 20px 60px rgba(255, 140, 66, 0.3);
  }
  50% {
    box-shadow: 0 20px 60px rgba(255, 140, 66, 0.5);
  }
}

.card-inner {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 20px;
  width: 100%;
  text-align: center;
}

.card-icon-wrap {
  margin-bottom: 10px;
}

.card-icon {
  width: 90px;
  height: 90px;
  border-radius: 28px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: transform 0.3s ease;
}

.icon-emoji {
  font-size: 48px;
  line-height: 1;
}

.card-en {
  font-size: 32px;
  font-weight: 800;
  color: var(--text);
  letter-spacing: 2px;
}

.card-zh {
  font-size: 18px;
  color: var(--text-light);
  transition: opacity 0.3s ease;
}

.card-zh.hidden {
  opacity: 0;
}

/* 5️⃣ 底部 Dock 控制栏 */
.dock-bar {
  position: fixed;
  bottom: 15px;
  left: calc(280px + (100vw - 280px) / 2);
  transform: translateX(-50%);
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 20px;
  padding: 0 50px;
  width: 850px;
  height: 110px;
  background: rgba(255, 255, 255, 0.8);
  backdrop-filter: blur(20px);
  -webkit-backdrop-filter: blur(20px);
  border: 1px solid rgba(255, 255, 255, 0.9);
  border-radius: 9999px;
  z-index: 9999;
}

.dock-bar > .dock-item {
  flex: 1;
  min-width: 0;
}

.dock-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 18px 24px;
  background: transparent;
  border: none;
  border-radius: 20px;
  color: var(--text-light);
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
}

.dock-item svg {
  font-size: 22px;
}

.dock-item:hover {
  background: rgba(0, 0, 0, 0.05);
  color: var(--text);
}

.dock-item.active {
  color: var(--primary);
  background: rgba(255, 140, 66, 0.1);
}

.dock-item.main-btn {
  width: 90px;
  height: 90px;
  background: linear-gradient(135deg, #FF8C42, #FF6B35);
  color: white;
  border-radius: 50%;
  padding: 0;
  margin: 0 12px;
  box-shadow: 0 8px 30px rgba(255, 140, 66, 0.4);
  font-size: 22px;
  justify-content: center;
}

.dock-item.main-btn:hover {
  transform: scale(1.08);
  box-shadow: 0 12px 40px rgba(255, 140, 66, 0.5);
}

.dock-item.main-btn:active {
  transform: scale(0.95);
}

/* 4️⃣ 全屏沉浸模式 */
.fullscreen-overlay {
  position: fixed;
  inset: 0;
  z-index: 1000;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(253, 251, 247, 0.92);
  backdrop-filter: blur(40px);
  -webkit-backdrop-filter: blur(40px);
}

.fullscreen-card {
  position: relative;
  width: 90vw;
  max-width: 800px;
  height: 85vh;
  max-height: 700px;
  background: rgba(255, 255, 255, 0.9);
  backdrop-filter: blur(30px);
  border: 1px solid rgba(255, 255, 255, 0.9);
  border-radius: 60px;
  box-shadow: 0 40px 100px rgba(0, 0, 0, 0.15);
  overflow: hidden;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  animation: breathe 3s ease-in-out infinite;
}

@keyframes breathe {
  0%, 100% { transform: scale(1); }
  50% { transform: scale(1.01); }
}

.fullscreen-bg {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 50%;
  opacity: 0.3;
  border-radius: 60px 60px 0 0;
}

.close-btn {
  position: absolute;
  top: 24px;
  right: 24px;
  width: 56px;
  height: 56px;
  background: rgba(255, 255, 255, 0.8);
  border: 1px solid rgba(255, 255, 255, 0.9);
  border-radius: 50%;
  color: var(--text-light);
  font-size: 22px;
  cursor: pointer;
  transition: all 0.3s ease;
  z-index: 10;
}

.close-btn:hover {
  background: white;
  color: var(--text);
  transform: scale(1.1);
}

.fullscreen-content {
  position: relative;
  z-index: 5;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
}

.fs-icon-wrap {
  margin-bottom: 30px;
}

.fs-icon {
  width: 140px;
  height: 140px;
  border-radius: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  animation: iconBounce 0.6s ease;
}

.fs-emoji {
  font-size: 72px;
}

@keyframes iconBounce {
  0% { transform: scale(0.5); opacity: 0; }
  50% { transform: scale(1.1); }
  100% { transform: scale(1); opacity: 1; }
}

.fs-en {
  font-size: 96px;
  font-weight: 900;
  color: var(--text);
  letter-spacing: 6px;
  margin: 20px 0;
  cursor: pointer;
  transition: transform 0.2s ease;
}

.fs-en:hover {
  transform: scale(1.05);
}

.fs-zh {
  font-size: 36px;
  color: var(--text-light);
  margin-bottom: 30px;
  transition: opacity 0.3s ease;
}

.fs-zh.hidden {
  opacity: 0;
}

.fs-hint {
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 16px;
  color: #AAA;
  animation: fadeInUp 0.5s ease 0.3s both;
}

.hint-icon {
  animation: point 1s ease-in-out infinite;
}

@keyframes point {
  0%, 100% { transform: translateX(0); }
  50% { transform: translateX(8px); }
}

@keyframes fadeInUp {
  from { opacity: 0; transform: translateY(20px); }
  to { opacity: 1; transform: translateY(0); }
}

.nav-btn {
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  width: 64px;
  height: 64px;
  background: rgba(255, 255, 255, 0.9);
  border: 1px solid rgba(255, 255, 255, 0.9);
  border-radius: 50%;
  color: var(--text-light);
  font-size: 24px;
  cursor: pointer;
  transition: all 0.3s ease;
  z-index: 10;
}

.nav-btn:hover {
  background: white;
  color: var(--primary);
  transform: translateY(-50%) scale(1.1);
}

.nav-btn.prev { left: 30px; }
.nav-btn.next { right: 30px; }

.fs-progress {
  position: absolute;
  bottom: 30px;
  left: 50%;
  transform: translateX(-50%);
  font-size: 18px;
  font-weight: 600;
  color: var(--text-light);
}

/* 全屏过渡动画 */
.fullscreen-enter-active {
  animation: zoomIn 0.3s ease;
}

.fullscreen-leave-active {
  animation: zoomOut 0.3s ease;
}

@keyframes zoomIn {
  from { opacity: 0; transform: scale(0.9); }
  to { opacity: 1; transform: scale(1); }
}

@keyframes zoomOut {
  from { opacity: 1; transform: scale(1); }
  to { opacity: 0; transform: scale(0.9); }
}

/* 响应式 */
@media (max-width: 1200px) {
  .sidebar {
    width: 236px;
  }

  .nav-logo {
    padding: 20px 16px;
    gap: 10px;
  }

  .nav-logo .logo-icon {
    width: 42px;
    height: 42px;
    font-size: 20px;
  }

  .nav-logo .brand-name {
    font-size: 18px;
  }

  .nav-item {
    padding: 16px;
  }

  .dock-bar {
    left: calc(236px + (100vw - 236px) / 2);
  }

  .cards-grid {
    grid-template-columns: repeat(3, 1fr);
    gap: 30px;
  }

  .main-title {
    font-size: 52px;
  }
}

@media (max-width: 992px) {
  .cards-grid {
    grid-template-columns: repeat(2, 1fr);
    gap: 24px;
  }

  .main-content {
    padding: 40px 24px 120px;
  }

  .page-header {
    margin-bottom: 28px;
  }

  .main-title {
    font-size: 42px;
  }

  .word-card {
    border-radius: 40px;
    padding: 30px 24px;
    min-height: 280px;
  }

  .card-icon {
    width: 70px;
    height: 70px;
    border-radius: 22px;
  }

  .icon-emoji {
    font-size: 36px;
  }

  .card-en {
    font-size: 26px;
  }

  .fs-en {
    font-size: 72px;
  }

  .fs-zh {
    font-size: 28px;
  }
}

@media (max-width: 768px) {
  .content-wrapper {
    display: block;
    min-height: 100vh;
  }

  .sidebar {
    display: none;
  }

  .main-content {
    padding: 0 16px 120px;
  }

  .page-header {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 20;
    margin-bottom: 0;
    min-height: 56px;
    padding: calc(env(safe-area-inset-top, 0px) + 6px) 16px 6px;
    border-radius: 0;
    transition: background-color 0.2s ease, box-shadow 0.2s ease;
  }

  .page-header.is-scrolled {
    background: rgba(249, 247, 242, 0.96);
    box-shadow: 0 8px 20px rgba(45, 52, 54, 0.08);
  }

  .page-header-spacer {
    display: block;
    height: calc(env(safe-area-inset-top, 0px) + 72px);
  }

  .mobile-back-btn {
    display: inline-flex;
    left: 16px;
  }

  .main-title {
    font-size: 27px;
  }

  .cards-grid {
    grid-template-columns: repeat(2, 1fr);
    gap: 16px;
  }

  .word-card {
    border-radius: 30px;
    padding: 24px 16px;
    min-height: 220px;
  }

  .card-icon {
    width: 56px;
    height: 56px;
    border-radius: 16px;
  }

  .icon-emoji {
    font-size: 28px;
  }

  .card-en {
    font-size: 22px;
  }

  .card-zh {
    font-size: 14px;
  }

  .dock-bar {
    left: 50%;
    padding: 12px 20px;
    gap: 8px;
    bottom: 20px;
  }

  .dock-item {
    padding: 10px 12px;
    font-size: 10px;
  }

  .dock-item span {
    display: none;
  }

  .dock-item.main-btn {
    width: 52px;
    height: 52px;
    margin: 0 4px;
    font-size: 18px;
  }

  .fullscreen-card {
    width: 95vw;
    height: 90vh;
    border-radius: 40px;
  }

  .fs-en {
    font-size: 52px;
  }

  .fs-zh {
    font-size: 24px;
  }

  .fs-icon {
    width: 100px;
    height: 100px;
    border-radius: 28px;
  }

  .fs-emoji {
    font-size: 52px;
  }

  .nav-btn {
    width: 48px;
    height: 48px;
    font-size: 18px;
  }

  .nav-btn.prev { left: 12px; }
  .nav-btn.next { right: 12px; }
}

/* 移动端适配 */
@media (max-width: 576px) {
  .dock-bar {
    width: 95%;
    height: 80px;
    bottom: 15px;
    gap: 4px;
    padding: 0 12px;
    border-radius: 40px;
  }

  .dock-item {
    padding: 6px 4px;
    font-size: 10px;
    flex: 1;
  }

  .dock-item svg {
    font-size: 20px;
  }

  .dock-item span {
    display: block;
    font-size: 9px;
    margin-top: 2px;
  }

  .dock-item.main-btn {
    width: 55px;
    height: 55px;
    margin: 0 4px;
    font-size: 18px;
    flex: 0 0 auto;
  }
}
</style>
